/**
 * Componentes UI estilo shadcn/ui para Chapería del Este
 * Importar individualmente: import { Button, Card, ... } from "@/components/ui"
 */
import { cn } from "@/lib/utils";
import { forwardRef, useState, useRef, useEffect } from "react";
import { X, Loader2, ChevronDown, Search, Plus } from "lucide-react";

// ─── Button ───────────────────────────────────────────────────────────────────
const BUTTON_VARIANTS = {
  default:     "bg-primary text-primary-foreground hover:bg-primary/90",
  destructive: "bg-destructive text-white hover:bg-destructive/90",
  outline:     "border border-border bg-transparent hover:bg-secondary text-foreground",
  secondary:   "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  ghost:       "hover:bg-secondary hover:text-foreground text-muted-foreground",
  link:        "text-primary underline-offset-4 hover:underline p-0 h-auto",
};
const BUTTON_SIZES = {
  default: "h-10 sm:h-9 px-4 py-2 text-sm",
  sm:      "h-8 sm:h-7 px-3 text-xs rounded-md",
  lg:      "h-12 sm:h-11 px-6 text-base",
  icon:    "h-11 w-11 sm:h-9 sm:w-9",
};

export const Button = forwardRef(function Button(
  { className, variant = "default", size = "default", loading, children, disabled, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        BUTTON_VARIANTS[variant] ?? BUTTON_VARIANTS.default,
        BUTTON_SIZES[size]       ?? BUTTON_SIZES.default,
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
});

// ─── Card ────────────────────────────────────────────────────────────────────
export function Card({ className, ...props }) {
  return (
    <div
      className={cn("rounded-lg border border-border bg-card text-card-foreground shadow-sm", className)}
      {...props}
    />
  );
}
export function CardHeader({ className, ...props }) {
  return <div className={cn("flex flex-col space-y-1 p-5 pb-3", className)} {...props} />;
}
export function CardTitle({ className, ...props }) {
  return <h3 className={cn("font-semibold font-display text-base leading-tight", className)} {...props} />;
}
export function CardContent({ className, ...props }) {
  return <div className={cn("p-5 pt-0", className)} {...props} />;
}

// ─── Badge ───────────────────────────────────────────────────────────────────
const BADGE_VARIANTS = {
  default:     "bg-primary/10 text-primary border-primary/20",
  success:     "bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.2)]",
  warning:     "bg-[hsl(var(--warning)/0.1)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.2)]",
  destructive: "bg-destructive/10 text-destructive border-destructive/20",
  secondary:   "bg-secondary text-secondary-foreground border-border",
  outline:     "bg-transparent text-foreground border-border",
};

export function Badge({ className, variant = "default", ...props }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        BADGE_VARIANTS[variant] ?? BADGE_VARIANTS.default,
        className
      )}
      {...props}
    />
  );
}

// ─── Input ───────────────────────────────────────────────────────────────────
export const Input = forwardRef(function Input({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        "flex h-10 sm:h-9 w-full rounded-md border border-input bg-background px-3 py-1",
        "text-base sm:text-sm placeholder:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
});

// ─── Textarea ────────────────────────────────────────────────────────────────
export const Textarea = forwardRef(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2",
        "text-base sm:text-sm placeholder:text-muted-foreground resize-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
});

// ─── Label ───────────────────────────────────────────────────────────────────
export function Label({ className, ...props }) {
  return (
    <label
      className={cn("text-sm font-medium leading-none text-foreground", className)}
      {...props}
    />
  );
}

// ─── Select ──────────────────────────────────────────────────────────────────
export const Select = forwardRef(function Select({ className, children, ...props }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          "flex h-10 sm:h-9 w-full appearance-none rounded-md border border-input bg-background",
          "px-3 pr-8 py-1 text-base sm:text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
    </div>
  );
});

// ─── SearchableSelect ─────────────────────────────────────────────────────────
// options: [{ value, label, sublabel? }]
export function SearchableSelect({ value, onChange, options = [], placeholder = "Seleccionar…", className, required, onCreateNew, createNewLabel = "Crear nuevo…" }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const containerRef      = useRef(null);
  const inputRef          = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = query.trim()
    ? options.filter((o) =>
        o.label?.toLowerCase().includes(query.toLowerCase()) ||
        o.sublabel?.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function selectOption(opt) {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  }

  function handleOpen() {
    setOpen((v) => !v);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          "flex h-10 sm:h-9 w-full items-center justify-between rounded-md border border-input bg-background",
          "px-3 py-1 text-base sm:text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          !selected && "text-muted-foreground"
        )}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {selected?.sublabel && (
            <span className="text-xs text-muted-foreground font-mono">{selected.sublabel}</span>
          )}
          <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar…"
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1 scrollbar-thin">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => selectOption(opt)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 cursor-pointer text-sm hover:bg-secondary transition-colors",
                    String(opt.value) === String(value) && "bg-primary/10 text-primary font-medium"
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {opt.sublabel && (
                    <span className="text-xs text-muted-foreground font-mono ml-2 shrink-0">{opt.sublabel}</span>
                  )}
                </li>
              ))
            )}
          </ul>
          {onCreateNew && (
            <div className="border-t border-border p-1.5">
              <button
                type="button"
                onClick={() => { setOpen(false); setQuery(""); onCreateNew(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-secondary rounded transition-colors"
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                {createNewLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── FormField ───────────────────────────────────────────────────────────────
export function FormField({ label, error, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label>{label}</Label>}
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ─── Dialog (modal) ──────────────────────────────────────────────────────────
export function Dialog({ open, onClose, title, children, className, size = "md" }) {
  // On mobile: bottom sheet. On sm+: centered modal.
  const sizes = { sm: "sm:max-w-md", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl", "2xl": "sm:max-w-5xl" };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className={cn(
          "relative z-10 w-full bg-card shadow-xl",
          "rounded-t-2xl sm:rounded-lg border border-border",
          "flex flex-col max-h-[92vh] sm:max-h-[90vh]",
          sizes[size] ?? sizes.md,
          className
        )}
      >
        {/* Handle bar for mobile */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden shrink-0">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 sm:py-4 border-b border-border shrink-0">
          <h2 className="font-semibold font-display text-base">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Sheet (panel lateral deslizable) ────────────────────────────────────────
export function Sheet({ open, onClose, title, children, side = "right" }) {
  const sideClass = {
    right:  "right-0 top-0 h-full w-full sm:w-[480px] sm:rounded-l-lg",
    bottom: "bottom-0 left-0 w-full h-[80vh] rounded-t-2xl",
  };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className={cn(
          "absolute z-10 bg-card border-border border shadow-xl flex flex-col",
          sideClass[side]
        )}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h2 className="font-semibold font-display text-base">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────────────
export function Table({ className, ...props }) {
  return (
    <div className="w-full overflow-auto">
      <table
        className={cn("w-full caption-bottom text-sm border-collapse", className)}
        {...props}
      />
    </div>
  );
}
export function TableHeader({ ...props }) { return <thead {...props} />; }
export function TableBody({ ...props })   { return <tbody {...props} />; }
export function TableRow({ className, ...props }) {
  return (
    <tr
      className={cn("border-b border-border transition-colors hover:bg-secondary/40", className)}
      {...props}
    />
  );
}
export function TableHead({ className, ...props }) {
  return (
    <th
      className={cn(
        "h-9 px-3 text-left align-middle font-medium text-muted-foreground text-xs uppercase tracking-wide",
        className
      )}
      {...props}
    />
  );
}
export function TableCell({ className, ...props }) {
  return <td className={cn("px-3 py-2.5 align-middle", className)} {...props} />;
}

// ─── StatCard (KPI) ──────────────────────────────────────────────────────────
export function StatCard({ title, value, sub, icon: Icon, accent = false, className }) {
  return (
    <Card className={cn(accent && "border-accent/40 bg-accent/5", className)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{title}</p>
            <p className="font-mono text-2xl font-semibold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          {Icon && (
            <div className={cn(
              "p-2 rounded-md",
              accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"
            )}>
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Spinner ─────────────────────────────────────────────────────────────────
export function Spinner({ className }) {
  return <Loader2 className={cn("animate-spin text-primary", className ?? "w-6 h-6")} />;
}

// ─── Empty state ─────────────────────────────────────────────────────────────
export function Empty({ message = "Sin datos", icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
      {Icon && <Icon className="w-10 h-10 opacity-30" />}
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ─── PageHeader ──────────────────────────────────────────────────────────────
export function PageHeader({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div className="min-w-0">
        <h1 className="font-display font-bold text-lg sm:text-xl">{title}</h1>
        {sub && <p className="text-sm text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  );
}

// ─── SearchInput ─────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = "Buscar…", className }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-8"
      />
    </div>
  );
}
